import React from 'react';
import { useI18n } from '../contexts/I18nContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { formatDateTime } from '../lib/utils';
import { Plus, Trash2, FileText } from 'lucide-react';

const sourceLabel = (source, t) => {
  if (source === 'soia') return t('shipments.noteSourceSoia');
  if (source === 'outlook') return t('shipments.noteSourceOutlook');
  if (source === 'system') return t('shipments.noteSourceSystem');
  return t('shipments.noteSourceManual');
};

const sourceClass = (source) => {
  if (source === 'soia') return 'bg-amber-500/20 text-amber-700 font-semibold';
  if (source === 'outlook') return 'bg-blue-500/20 text-blue-700';
  if (source === 'system') return 'bg-gray-500/20 text-gray-700';
  return 'bg-secondary text-muted-foreground';
};

export function NotesPanel({
  notes,
  containers,
  newNote,
  setNewNote,
  onAdd,
  onDelete,
  isReadOnly,
}) {
  const { t } = useI18n();
  return (
    <>
      {!isReadOnly && (
        <div className="bg-card border border-border rounded-sm p-4 space-y-4">
          <h3 className="font-heading text-lg">{t('shipments.newInternalNote')}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>{t('shipments.noteLabel')}</Label>
              <Textarea
                value={newNote.note}
                onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
                placeholder={t('shipments.notePlaceholder')}
                className="mt-1"
                rows={3}
                data-testid="note-input"
              />
            </div>
            <div className="space-y-4">
              <div>
                <Label>{t('shipments.containerOptional')}</Label>
                <Select
                  value={newNote.container_number}
                  onValueChange={(v) => setNewNote({ ...newNote, container_number: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t('shipments.blLevel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bl">{t('shipments.blLevel')}</SelectItem>
                    {containers.map((c) => (
                      <SelectItem key={c.container_number} value={c.container_number}>
                        {c.container_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={onAdd} className="w-full" data-testid="add-note-btn">
                <Plus size={16} className="mr-1" />
                {t('shipments.addNote')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {notes.length === 0 ? (
          <div className="bg-card border border-border rounded-sm p-8 text-center">
            <FileText className="mx-auto w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {isReadOnly ? t('shipments.noClientNotes') : t('shipments.noInternalNotes')}
            </p>
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className={`bg-card border rounded-sm p-4 ${note.source === 'soia' ? 'border-amber-500/40' : 'border-border'}`}
              data-testid={`note-${note.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {note.container_number && (
                      <span className="text-xs bg-secondary px-2 py-0.5 rounded font-mono">
                        {note.container_number}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded ${sourceClass(note.source)}`}>
                      {sourceLabel(note.source, t)}
                    </span>
                    {note.visible_to_client && !isReadOnly && (
                      <span className="text-xs bg-green-500/20 text-green-700 px-2 py-0.5 rounded">
                        {t('shipments.visibleToClientBadge')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                  <p className="text-xs text-muted-foreground mt-2">{formatDateTime(note.created_at)}</p>
                </div>
                {!isReadOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(note.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
